import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {DunningService} from '../../../service/api/dunning.service';
import {BdManualContractDecisionDto} from '../../../model/dto/api/bd-manual-contract-decision.dto';
import {BdDunningDto} from '../../../model/dto/api/bd-dunning.dto';
import {extractError} from '../../../util/error-extraction.util';

/**
 * Eine der vier möglichen Reaktionen auf einen Plan-Bruch.
 */
type BdPlanBreakOption = 'CONTINUE' | 'PAUSE' | 'HANDOVER' | 'WRITE_OFF';

/**
 * AI:
 * Status: 'generated'
 * Type: Component
 * SubType: Shared / Dialog
 * Reason: Pending-Action-Dialog für die Reaktion des Sachbearbeiters auf einen
 *         Plan-Bruch (status = BREACHED). Bietet 4 Aktionen:
 *           - Mahnung fortsetzen   -> decideManualContractDecision(action='CONTINUE', comment)
 *           - Mahnung pausieren    -> blendet stattdessen den Pause-Dialog ein
 *           - Inkasso übergeben    -> decideManualContractDecision(action='HANDOVER', comment)
 *           - Forderung abschreiben -> decideManualContractDecision(action='WRITE_OFF', comment)
 *
 * @ticket TM-1000001782
 */
@Component({
    selector: 'mvs-bd-plan-break-decision-dialog',
    templateUrl: './bd-plan-break-decision-dialog.component.html',
    styleUrls: ['./bd-plan-break-decision-dialog.component.scss'],
    standalone: false
})
export class BdPlanBreakDecisionDialogComponent implements OnInit {

    // ------------------------------------------------------------------ //
    // Inputs
    // ------------------------------------------------------------------ //

    @Input() dunning: BdDunningDto | null = null;
    @Input() uiComponentConfig: string | null = null;

    // ------------------------------------------------------------------ //
    // Outputs
    // ------------------------------------------------------------------ //

    @Output() actionCompleted: EventEmitter<void> = new EventEmitter<void>();
    @Output() actionCancelled: EventEmitter<void> = new EventEmitter<void>();

    // ------------------------------------------------------------------ //
    // State
    // ------------------------------------------------------------------ //

    initialized: boolean = false;
    visible: boolean = true;

    /** Anzeige-Felder für den Mahnungs-Header. */
    customerName: string = '—';
    dunningTypeName: string = '—';
    activeLevelLabel: string = '—';

    /** Aktuell ausgewählte Option (initial null = nichts gewählt). */
    selectedOption: BdPlanBreakOption | null = null;

    /** Optionaler Kommentar zur Entscheidung. */
    comment: string = '';

    /** Wechselt auf {@code true}, sobald 'Pause' gewählt wurde und delegiert an mvs-bd-pause-dialog. */
    pauseDialogVisible: boolean = false;

    submitting: boolean = false;
    errorMessage: string | null = null;

    /** Liste der angezeigten Optionen für die Radio-Group. */
    readonly options: ReadonlyArray<{ value: BdPlanBreakOption; label: string; icon: string }> = [
        {value: 'CONTINUE', label: 'Mahnung fortsetzen', icon: 'fa-regular fa-play'},
        {value: 'PAUSE', label: 'Mahnung pausieren', icon: 'fa-regular fa-pause'},
        {value: 'HANDOVER', label: 'Inkasso übergeben', icon: 'fa-regular fa-handshake'},
        {value: 'WRITE_OFF', label: 'Forderung abschreiben', icon: 'fa-regular fa-eraser'}
    ];

    // ------------------------------------------------------------------ //
    // Constructor
    // ------------------------------------------------------------------ //

    constructor(private readonly dunningService: DunningService) {
    }

    // ------------------------------------------------------------------ //
    // Lifecycle
    // ------------------------------------------------------------------ //

    ngOnInit(): void {
        this.initComponent();
    }

    private initComponent(): void {
        this.customerName = this.buildCustomerName(this.dunning);
        this.dunningTypeName = this.readString(this.dunning, ['dunningTypeName', 'typeName']) ?? '—';
        this.activeLevelLabel = this.readString(this.dunning, ['activeLevelShortName', 'activeLevelName']) ?? '—';
        this.initialized = true;
    }

    // ------------------------------------------------------------------ //
    // Actions
    // ------------------------------------------------------------------ //

    /** Vom Benutzer ausgewählte Option speichern. */
    selectOption(option: BdPlanBreakOption): void {
        this.selectedOption = option;
        this.errorMessage = null;
    }

    /**
     * Sendet die gewählte Aktion ans Backend (oder öffnet den Pause-Dialog).
     */
    confirm(): void {
        if (this.selectedOption == null) {
            this.errorMessage = 'Bitte eine Aktion auswählen.';
            return;
        }

        if (this.selectedOption === 'PAUSE') {
            this.openPauseSubDialog();
            return;
        }

        this.dispatchManualContractDecision(this.selectedOption);
    }

    /** Wird vom eingebetteten Pause-Dialog beim Schließen ausgelöst. */
    onPauseCompleted(): void {
        this.pauseDialogVisible = false;
        this.closeDialog();
        this.actionCompleted.emit();
    }

    onPauseCancelled(): void {
        this.pauseDialogVisible = false;
    }

    /** Benutzer-Abbruch über den X-Button. */
    onCancel(): void {
        this.closeDialog();
        this.actionCancelled.emit();
    }

    onVisibleChange(visible: boolean): void {
        if (!visible) {
            this.actionCancelled.emit();
        }
    }

    // ------------------------------------------------------------------ //
    // Backend dispatch
    // ------------------------------------------------------------------ //

    /**
     * Ruft {@code DunningService.decideManualContractDecision} mit der gewählten Aktion auf.
     */
    private dispatchManualContractDecision(option: 'CONTINUE' | 'HANDOVER' | 'WRITE_OFF'): void {
        const dunningId = this.resolveDunningId();
        if (dunningId == null) {
            this.errorMessage = 'Keine Mahnungs-ID verfügbar.';
            return;
        }

        const decision: BdManualContractDecisionDto = {
            action: option,
            comment: this.comment?.trim() || undefined
        };

        this.errorMessage = null;
        this.submitting = true;

        this.dunningService.decideManualContractDecision(dunningId, decision).subscribe({
            next: () => {
                this.submitting = false;
                this.closeDialog();
                this.actionCompleted.emit();
            },
            error: (err: unknown) => {
                this.submitting = false;
                this.errorMessage = extractError(err);
            }
        });
    }

    private openPauseSubDialog(): void {
        this.pauseDialogVisible = true;
    }

    // ------------------------------------------------------------------ //
    // Helpers
    // ------------------------------------------------------------------ //

    private closeDialog(): void {
        this.visible = false;
    }

    private resolveDunningId(): number | null {
        if (!this.dunning) {
            return null;
        }
        const id = (this.dunning.id ?? this.dunning['dunningId']) as unknown;
        return typeof id === 'number' && id > 0 ? id : null;
    }

    private buildCustomerName(dunning: BdDunningDto | null): string {
        if (!dunning) {
            return '—';
        }
        const first = (dunning.personFirstName ?? dunning.firstName ?? '') as string;
        const last = (dunning.personLastName ?? dunning.lastName ?? '') as string;
        const combined = `${first} ${last}`.trim();
        return combined.length > 0 ? combined : '—';
    }

    private readString(source: BdDunningDto | null, keys: string[]): string | null {
        if (!source) {
            return null;
        }
        for (const key of keys) {
            const value = source[key];
            if (typeof value === 'string' && value.length > 0) {
                return value;
            }
        }
        return null;
    }
}
